<template>
    <div class="ask-exchange" data-testid="ask-box-exchange">
        <div class="ask-exchange__asked">
            <p
                class="ask-exchange__question"
                data-testid="ask-box-exchange-question"
            >
                {{ question }}
            </p>
            <!-- Which version the answer described, at the far end of the
                 question line like a timestamp — so the answer itself stays
                 clean. Interpolated, never v-html: the title is customer
                 text. -->
            <span
                v-if="versionLabel"
                class="ask-exchange__version"
                data-testid="ask-box-exchange-version"
                :title="versionDetail"
                >{{ versionLabel }}</span
            >
        </div>

        <AskAnswer
            :content="answer.answer"
            :references="answer.references ?? []"
            data-testid="ask-box-exchange-answer"
            @navigate="emit('navigate')"
        />

        <!-- The version note goes to the offer too: an answer about a draft
             adds to that draft, however long ago it was given. -->
        <AskThreadOffer
            v-if="answer.offer_thread"
            :offer="answer.offer_thread"
            :message="answer.offer_thread.message ?? question"
            :references="answer.references ?? []"
            :version="answer.version"
            @handed-off="emit('handedOff')"
        />
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import AskAnswer from "@/components/AskBox/AskAnswer.vue";
import AskThreadOffer from "@/components/AskBox/AskThreadOffer.vue";
import {
    versionDetail as versionDetailOf,
    versionNote,
} from "@/components/AskBox/ask-content";
import type { AskAnswer as AskAnswerPayload } from "@/lib/openapi/gen/types.gen";

/**
 * One finished question and its answer, as it sits above the live exchange.
 *
 * Its own component because a past exchange is not a lesser thing than the
 * current one: the links still go where they went, and a thread offered two
 * questions ago is still an offer worth taking — someone reads the answer,
 * thinks about it, asks one more thing, and only then decides to open the
 * thread. Rendering these as inert text would take the decision away at the
 * moment they were closest to making it.
 *
 * The live exchange keeps its own markup in `AskBox`, because it has two states
 * this one cannot have — thinking, and failed — and folding those in here would
 * mean a component that is mostly branches about which of three things it is.
 */
const props = defineProps<{
    question: string;
    answer: AskAnswerPayload;
}>();

const versionLabel = computed(() => versionNote(props.answer.version));
const versionDetail = computed(() => versionDetailOf(props.answer.version));

// `handedOff`, spelled as `AskThreadOffer` spells its own — the two sit beside
// each other and Vue accepts either, so the only thing a second spelling buys a
// reader is a moment wondering whether the difference means something.
const emit = defineEmits<{
    navigate: [];
    handedOff: [];
}>();
</script>

<style scoped>
/* The whole exchange is dimmed, not just its prose.

   With the fade on the answer alone, a past exchange's entity links and its
   "start a thread" button drew at full strength beside the live one's — so a
   sitting with three refusals in it showed three identical blue buttons and
   nothing said which one answered the question you just asked. Confirmed in
   the browser, not deduced: the buttons are siblings of the answer, not
   children, so they never inherited it.

   Deliberately shallow. These are still live controls — a thread offered two
   questions ago is still worth taking — and anything heavier would read as
   disabled rather than earlier. */
.ask-exchange {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    opacity: 0.75;
}

/* The same muted treatment the live question gets: context, not content, and it
   must not compete with the answer under it. */
.ask-exchange__asked {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
}

.ask-exchange__question {
    margin: 0;
    color: var(--p-text-muted-color);
    font-size: 0.85rem;
}

/* The same weight as the esc hint: present if you look, silent if you do not.
   One word; the draft's title is on hover. The ellipsis is only for a box too
   narrow for even that. */
.ask-exchange__version {
    flex: 0 1 auto;
    max-width: 55%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--p-text-muted-color);
    font-size: 0.7rem;
    letter-spacing: 0.02em;
    opacity: 0.6;
}

/* The answer's own type lives in `AskAnswer`, shared with the live one; the
   fade on `.ask-exchange` is the only difference between a past answer and
   the current one. These are still readable answers someone may be working
   from — it says which one is current, not that the rest are finished with. */
</style>
